import React, { createContext, useContext, useMemo } from "react";
import { useLicenseStatusQuery } from "hooks/queries/useDashboardQueries";
import { getApiErrorMessage } from "utils/helpers";
import { CompanyDashboard } from "types/dashboard";

export interface LicenseStatusValue {
  isExpired: boolean;
  isLoading: boolean;
  error: string | null;
  remainingDays: number;
  hasLicense: boolean;
  companyData: CompanyDashboard | null;
}

const defaultValue: LicenseStatusValue = {
  isExpired: false,
  isLoading: false,
  error: null,
  remainingDays: 0,
  hasLicense: false,
  companyData: null,
};

const LicenseStatusContext = createContext<LicenseStatusValue>(defaultValue);

export const LicenseStatusProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const query = useLicenseStatusQuery();

  const value = useMemo(
    (): LicenseStatusValue => ({
      isExpired: query.isExpired,
      isLoading: query.isLoading,
      error: query.error
        ? getApiErrorMessage(query.error, "Failed to load dashboard")
        : null,
      remainingDays: query.remainingDays,
      hasLicense: query.hasLicense,
      companyData: query.companyData,
    }),
    [
      query.isExpired,
      query.isLoading,
      query.error,
      query.remainingDays,
      query.hasLicense,
      query.companyData,
    ]
  );

  return (
    <LicenseStatusContext.Provider value={value}>
      {children}
    </LicenseStatusContext.Provider>
  );
};

export const useLicenseStatusContext = (): LicenseStatusValue =>
  useContext(LicenseStatusContext);
